// Cadenas de hashes por disparo y por vídeo (ADR 0004 §4.1 y §4.3), idénticas a `@kyp/core`.
import { concatBytes, fromHex, sha256, toHex, u32, u64, utf8 } from './crypto.js';
import type { CaptureVideoKind } from './types.js';

/** Firma periódica del vídeo cada N trozos (ADR 0004 §4.3). */
export const VIDEO_SIGN_EVERY = 5;

/** Eslabón: huella del binario (64 hex) y su instante monotónico (µs). */
export interface ChainItem {
  sha256: string;
  t_mono: number;
}

/**
 * Cadena incremental: `h_k = H(h_{k-1} ‖ unhex(sha256_k) ‖ u64(t_mono_k))`.
 *
 * ```ts
 * const chain = await HashChain.forShot(sessionId, serverNonce, i);
 * await chain.push({ sha256, t_mono }); chain.root; // hex de h_n
 * ```
 */
export class HashChain {
  private state: Uint8Array;
  private count = 0;

  private constructor(start: Uint8Array) {
    this.state = start;
  }

  /** Cadena de un disparo: `h_0 = H(utf8(session_id) ‖ unhex(server_nonce) ‖ u32(i))`. */
  static async forShot(sessionId: string, serverNonce: string, index: number): Promise<HashChain> {
    return new HashChain(
      await sha256(concatBytes(utf8(sessionId), fromHex(serverNonce), u32(index))),
    );
  }

  /** Cadena de un vídeo: `h_0 = H(utf8(session_id) ‖ unhex(server_nonce) ‖ utf8(kind))`. */
  static async forVideo(
    sessionId: string,
    serverNonce: string,
    kind: CaptureVideoKind,
  ): Promise<HashChain> {
    return new HashChain(
      await sha256(concatBytes(utf8(sessionId), fromHex(serverNonce), utf8(kind))),
    );
  }

  /** Añade un eslabón y devuelve el nuevo estado en hex. */
  async push(item: ChainItem): Promise<string> {
    if (!/^[0-9a-f]{64}$/.test(item.sha256)) throw new TypeError('invalid_sha256_hex');
    this.state = await sha256(concatBytes(this.state, fromHex(item.sha256), u64(item.t_mono)));
    this.count++;
    return this.root;
  }

  /** Estado actual en hex (tras `length` eslabones). */
  get root(): string {
    return toHex(this.state);
  }

  /** Número de eslabones añadidos. */
  get length(): number {
    return this.count;
  }
}

/** Raíz de la cadena de un disparo. */
export async function shotChainRoot(
  sessionId: string,
  serverNonce: string,
  index: number,
  frames: readonly ChainItem[],
): Promise<string> {
  const chain = await HashChain.forShot(sessionId, serverNonce, index);
  for (const f of frames) await chain.push(f);
  return chain.root;
}

/** Raíz de la cadena de un vídeo. */
export async function videoChainRoot(
  sessionId: string,
  serverNonce: string,
  kind: CaptureVideoKind,
  chunks: readonly ChainItem[],
): Promise<string> {
  const chain = await HashChain.forVideo(sessionId, serverNonce, kind);
  for (const c of chunks) await chain.push(c);
  return chain.root;
}

/** `upto` de las firmas de un vídeo de `n` trozos: `[4, 9, …]` (< n−1) y `n−1`. */
export function videoCheckpoints(n: number): number[] {
  if (n <= 0) return [];
  const out: number[] = [];
  for (let u = VIDEO_SIGN_EVERY - 1; u < n - 1; u += VIDEO_SIGN_EVERY) out.push(u);
  out.push(n - 1);
  return out;
}

/** Estados de la cadena tras cada `upto` de `videoCheckpoints` (hex de h_{upto+1}). */
export async function videoChainPrefixes(
  sessionId: string,
  serverNonce: string,
  kind: CaptureVideoKind,
  chunks: readonly ChainItem[],
): Promise<{ upto: number; hash: string }[]> {
  const marks = new Set(videoCheckpoints(chunks.length));
  const chain = await HashChain.forVideo(sessionId, serverNonce, kind);
  const out: { upto: number; hash: string }[] = [];
  for (const [k, c] of chunks.entries()) {
    const hash = await chain.push(c);
    if (marks.has(k)) out.push({ upto: k, hash });
  }
  return out;
}

/** X e Y de la ráfaga del disparo `index` (ADR 0004 §3). */
export { deriveBurst } from './burst.js';
